import { Injectable, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, forkJoin, Observable, of, Subject, Subscription} from 'rxjs';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {TranslateService} from '@ngx-translate/core';
import {map, shareReplay, switchMap, take, tap} from 'rxjs/operators';

import {FeatureFlags, FeatureFlagService, OngoingRequestTrackerService, SessionService} from '@core';
import {BaseResult, NotificationService} from '@shared';

import {VisitList} from 'planner-shared';

import {ProblemInstanceService} from '../problem-instance/problem-instance.service';
import {ManualRouteInformation, RoutesResult} from '../../models';
import {RouteJobIdPair} from "../../models/route-job-pair.model";
import {RouteTaskService} from "../../../route-planning/tasks/services/route-task.service";

@Injectable({providedIn: 'root'})
export class RoutesService {
    private readonly httpClient = inject(HttpClient);
    private readonly sessionService = inject(SessionService);
    private readonly notificationService = inject(NotificationService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly translateService = inject(TranslateService);
    private readonly ongoingRequestTracker = inject(OngoingRequestTrackerService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly routeTaskService = inject(RouteTaskService);

    private manualRouteChange = new Subject<void>();
    private setRoutesSubscription: Subscription;

    private readonly routeResultsPerJobs: BehaviorSubject<RouteJobIdPair[]> = new BehaviorSubject<RouteJobIdPair[]>([]);
    private independentSyncTasksFeatureEnabled: boolean;

    constructor() {
        this.featureFlagService.isEnabled(FeatureFlags.IndependentSyncTasks)
            .pipe(
                take(1),
                tap(flagValue => this.independentSyncTasksFeatureEnabled = flagValue)
            ).subscribe();
    }

    public getRoutes(jobId?: string): Observable<RoutesResult> {
        jobId = jobId ?? this.sessionService.getJobId();
        if (!jobId) {
            this.problemInstanceService.getProblemInstance().subscribe(instanceResult => {
                jobId = instanceResult.problemInstance.jobId;
                if (jobId) {
                    this.sessionService.setJobId(jobId);
                }
            });
        }
        if (!jobId) {
            return of(<RoutesResult>{});
        }

        const url = `${this.sessionService.getApiUrl()}/route/${jobId}`;
        const requestOptions = this.ongoingRequestTracker.createTrackingEnabledRequestOptions();

        return this.httpClient.get<RoutesResult>(url, requestOptions).pipe(
            tap(routeResult => {
                this.updateLocalRoutes(routeResult, jobId);
            }),
            shareReplay(1)
        );
    }

    public getLocalRoutes(jobId?: string): Observable<RoutesResult> {
        if (!jobId) {
            jobId = this.sessionService.getJobId();
            if (!jobId) {
                return of(<RoutesResult>{});
            }
        }
        return this.routeResultsPerJobs.pipe(
            map(routePairs => {
                return routePairs.find(routePair => routePair.jobId === jobId).routesResult;
            })
        );
    }

    public existRoutes(): Observable<boolean> {
        return this.getRoutes().pipe(map(result => !result.optimizationRunning && result.routes != null));
    }

    public setRoutes(jobId?: string): Observable<BaseResult> {
        if (!jobId) {
            jobId = this.sessionService.getJobId();
        } else {
            this.sessionService.setJobId(jobId);
        }

        const url = `${this.sessionService.getApiUrl()}/set_routes/${this.sessionService.requestId}/${jobId}`;
        return this.httpClient.put<BaseResult>(url, {});
    }

    public deleteManualChanges(): Observable<BaseResult> {
        const jobId = this.sessionService.getJobId();
        const url = `${this.sessionService.getApiUrl()}/revert_manual_changes/${jobId}`;
        return this.httpClient.put<BaseResult>(url, {}).pipe(
            tap(() => {
                this.setRoutesSubscription?.unsubscribe();
                this.setRoutesSubscription = this.setRoutes().subscribe();
            })
        );
    }

    public sendTaskChangeToMrp(manualRouteInformationList: ManualRouteInformation[]): Observable<RoutesResult> {
        const url = `${this.sessionService.getApiUrl()}/manual_route_updates_v2/${this.sessionService.getJobId()}`;
        const body = {
            manualRouteInformationList
        };
        return this.httpClient.put<RoutesResult>(url, body).pipe(
            switchMap(routesResult => {
                return forkJoin({
                    routesResultResponse: of(routesResult),
                    setRoutesResponse: this.setRoutes()
                });
            }),
            tap(result => {
                this.setRoutesSubscription?.unsubscribe();
                this.setRoutesSubscription = of(result.setRoutesResponse).subscribe();
            }),
            map(result => {
                this.updateLocalRoutes(result.routesResultResponse);
                if (this.independentSyncTasksFeatureEnabled) {
                    if (this.routeTaskService
                        .estimateUnpairedRouteSyncTasks(result.routesResultResponse.routes.tasks).length > 0)
                    {
                        this.notificationService.warning("SYNCED_TASK_NOT_PAIRED_WARNING_MRP");
                    }
                }
                this.triggerManualRouteChange();
                return result.routesResultResponse;
            })
        );
    }

    public updateAndVerifyResult(
        routes: VisitList,
        event: CdkDragDrop<any>,
        onCompleteFunction: Function,
        notifyMovedTask = true
    ): void {
        const originalTaskId = event.item.data.originalTaskId;
        const taskId = event.item.data.routePlannerTaskId;
        const workHourId = event.item.data.workHourId;
        const previousTaskIndex = event.previousIndex;
        let hasChanged = true;
        if (workHourId) {
            const newTask = routes.tasks.filter(task => task.workHourId === workHourId)[previousTaskIndex];
            if (newTask && newTask.taskId === originalTaskId && newTask.routePlannerTaskId === taskId) {
                this.notificationService.warning('CANNOT_MOVE_TASK');
                hasChanged = false;
            }
        } else {
            if (
                routes.nonAllocatedTasksInfo.find(
                    task => task.taskId === originalTaskId && task.routePlannerTaskId === taskId
                )
            ) {
                this.notificationService.warning('CANNOT_MOVE_TASK');
                hasChanged = false;
            }
        }
        if (hasChanged) {
            const newTask = routes.tasks.find(
                task => task.taskId === originalTaskId && task.routePlannerTaskId === taskId
            );
            if (newTask && newTask.isSynced && !this.independentSyncTasksFeatureEnabled) {
                this.notifyChangesSynced(newTask.taskId, routes, notifyMovedTask, workHourId);
            } else if (notifyMovedTask && newTask) {
                this.problemInstanceService.getProblemInstance().subscribe(result => {
                    const employee = result.problemInstance.employeeWorkShifts.find(
                        ws => ws.workHourId === newTask.workHourId
                    ).employeeName;
                    this.notificationService.success(
                        this.translateService.instant('TASK_HAS_BEEN_MOVED') + employee,
                        null,
                        null,
                        {disableTimeOut: true}
                    );
                });
            }
        }
        onCompleteFunction();
    }

    public notifyChangesSynced(
        taskId: string,
        routes: VisitList,
        notifyMovedTask: boolean,
        originalWorkHourId: any
    ): void {
        this.problemInstanceService.getProblemInstance().subscribe(result => {
            const syncedWithAsDependent = result.problemInstance.syncedTaskPairs
                .filter(taskPair => taskPair.syncedWithTaskId === taskId)
                .map(taskPair => taskPair.syncedMasterTaskId);
            let masterTaskId: string;
            if (syncedWithAsDependent.length > 0) {
                masterTaskId = syncedWithAsDependent[0];
            } else {
                masterTaskId = taskId;
            }
            let syncedTaskIds = result.problemInstance.syncedTaskPairs
                .filter(taskPair => taskPair.syncedMasterTaskId === masterTaskId)
                .map(taskPair => taskPair.syncedWithTaskId)
                .concat(masterTaskId)
                .filter(syncedTaskId => (notifyMovedTask ? true : syncedTaskId !== taskId));
            if (originalWorkHourId) {
                syncedTaskIds = syncedTaskIds.filter(syncedTaskId => syncedTaskId === taskId);
            }
            routes.tasks
                .filter(task => syncedTaskIds.includes(task.taskId))
                .forEach(syncedTask => {
                    const employee = result.problemInstance.employeeWorkShifts.find(
                        ws => ws.workHourId === syncedTask.workHourId
                    ).employeeName;
                    this.notificationService.success(
                        this.translateService.instant('SYNCED_TASK_HAS_BEEN_MOVED') + employee,
                        null,
                        null,
                        {disableTimeOut: true}
                    );
                });
        });
    }

    public getManualRouteChangeListener(): Observable<void> {
        return this.manualRouteChange.asObservable();
    }

    public updateLocalRoutes(routes: RoutesResult, jobId?: string) {
        if (!jobId) {
            jobId = this.sessionService.getJobId();
        }

        if (this.routeResultsPerJobs.value.some(routePair => routePair.jobId === jobId)) {
            const index = this.routeResultsPerJobs.value.findIndex(routePair => routePair.jobId === jobId);
            this.routeResultsPerJobs.value[index] = {jobId, routesResult: routes};
        } else {
            this.routeResultsPerJobs.value.push({jobId, routesResult: routes});
        }
    }

    private triggerManualRouteChange(): void {
        this.manualRouteChange.next();
    }
}
